import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Container,
} from '@mui/material';
import Layout from '../components/Layout';

const PrivacyPolicy: React.FC = () => {
  const lastUpdated = "December 30, 2025";

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          {/* Privacy Policy Section */}
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: 'text.primary', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            PRIVACY POLICY FOR TEACHORA
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: {lastUpdated}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>1. Introduction</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Welcome to Teachora ("we," "our," or "us"). Teachora is a tutoring platform that connects students with qualified tutors for online learning sessions. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.
          </Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            By using Teachora, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>2. Information We Collect</Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>2.1 Information You Provide Directly</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            <p><strong>Account Information:</strong> Name, email address, phone number, profile photo</p>
            <p><strong>Profile Information:</strong> Educational background, subjects of interest, learning preferences, teaching experience (for tutors), qualifications, certifications</p>
            <p><strong>Payment Information:</strong> Payment method details, transaction history, wallet balance (processed through secure third-party payment processors)</p>
            <p><strong>Communication Data:</strong> Messages sent through our in-app messaging system, support requests, feedback</p>
            <p><strong>Booking Information:</strong> Class schedules, session preferences, tutor and student interactions</p>
            <p><strong>Content:</strong> Educational materials uploaded, reviews and ratings, profile descriptions</p>
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>2.2 Information Collected Automatically</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            <p><strong>Device Information:</strong> Device type, operating system, unique device identifiers, mobile network information</p>
            <p><strong>Usage Data:</strong> App features accessed, time spent on features, pages viewed, interactions with tutors and students</p>
            <p><strong>Location Data:</strong> Approximate location if location services are enabled for finding nearby tutors</p>
            <p><strong>Log Data:</strong> IP address, browser type, access times, dates and times of visits</p>
            <p><strong>Analytics Data:</strong> Learning progress, booking patterns, preferences, performance metrics</p>
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>2.3 Information from Third Parties</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            <p><strong>Authentication Services:</strong> Information from Google Sign-In including name, email, and profile picture</p>
            <p><strong>Payment Processors:</strong> Transaction confirmations and payment status from Razorpay and other payment gateways</p>
            <p><strong>Firebase Services:</strong> Authentication tokens and user verification status</p>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>3. How We Use Your Information</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            We use the collected information to:
            <ul>
              <li>Provide, maintain, and improve our tutoring platform</li>
              <li>Create and manage user accounts and verify identity</li>
              <li>Process payments, refunds, and wallet transactions</li>
              <li>Send notifications, reminders, and service-related communications</li>
              <li>Personalize user experience and recommend tutors or content</li>
              <li>Analyze usage patterns and improve platform performance</li>
              <li>Detect and prevent fraud, abuse, and security issues</li>
              <li>Comply with legal obligations and enforce terms of service</li>
              <li>Send marketing communications with user consent</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>4. Information Sharing and Disclosure</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            We do not sell your personal information. We may share information in the following situations:
            <ul>
              <li><strong>With Tutors or Students:</strong> Relevant information is shared when a class is booked</li>
              <li><strong>With Service Providers:</strong> Including payment processors, cloud storage, analytics, and customer support services</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety</li>
              <li><strong>Business Transfers:</strong> During mergers, acquisitions, or asset sales</li>
              <li><strong>With Your Consent:</strong> When you explicitly allow information sharing</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>5. Data Storage and Security</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            <ul>
              <li><strong>Storage:</strong> Data is stored on secure servers provided by Firebase and other trusted providers</li>
              <li><strong>Security Measures:</strong> Encryption, secure authentication, and access controls are used</li>
              <li><strong>Data Retention:</strong> Information is retained as long as necessary to provide services and meet legal requirements</li>
              <li><strong>International Transfers:</strong> Data may be processed outside your country of residence</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>6. Your Rights and Choices</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            You have the right to:
            <ul>
              <li>Access your personal data</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Request account and data deletion subject to legal requirements</li>
              <li>Request data portability</li>
              <li>Opt out of marketing communications</li>
              <li>Control location data through device settings</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>7. Children's Privacy</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Teachora is not intended for users under the age of 13. We do not knowingly collect personal information from children under 13. If such data is discovered, it will be deleted promptly.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>8. Third-Party Services</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Teachora integrates with third-party services including:
            <ul>
              <li>Google Services such as Google Sign-In, Firebase, and Google Maps</li>
              <li>Razorpay for payment processing</li>
              <li>Jitsi Meet for video calls</li>
              <li>YouTube for educational content</li>
            </ul>
            Each third-party service operates under its own privacy policy.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>9. Cookies and Tracking Technologies</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            We use cookies and similar technologies to track activity and improve user experience. You can manage cookie preferences through your device settings.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>10. Changes to This Privacy Policy</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            We may update this policy periodically. Updates will be posted with a revised "Last Updated" date. Continued use of Teachora indicates acceptance of the updated policy.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>11. Contact Us</Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            For questions or concerns regarding this Privacy Policy, contact us at:<br />
            <strong>Email:</strong> rameshbtechit2001@gmail.com
          </Typography>

          <Box sx={{ my: 8 }}>
            <Divider />
          </Box>

          {/* Refund Policy Section */}
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: 'text.primary', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            REFUND POLICY FOR TEACHORA
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last Updated: {lastUpdated}
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>1. Introduction</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            This Refund Policy outlines the terms and conditions for refunds and cancellations on the Teachora platform.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>2. General Refund Principles</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            <ul>
              <li>Refunds are subject to review and approval</li>
              <li>Refunds are processed to the original payment method</li>
              <li>Processing time is typically 5 to 10 business days after approval</li>
              <li>Payment gateway fees may apply</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>3. Class Booking Cancellations</Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>3.1 Student Cancellations</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            <ul>
              <li><strong>More than 24 hours before class:</strong> Full refund or wallet credit</li>
              <li><strong>12 to 24 hours before class:</strong> 50 percent refund or wallet credit</li>
              <li><strong>Less than 12 hours before class:</strong> No refund except exceptional cases</li>
              <li><strong>No-show:</strong> No refund</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>3.2 Tutor Cancellations</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            <ul>
              <li>Full refund or wallet credit to students</li>
              <li>Option to reschedule or change tutor</li>
              <li>Repeated cancellations may result in tutor account restrictions</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>3.3 Platform-Initiated Cancellations</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            Full refund for technical issues or tutor unavailability
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>4. Trial Classes</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            <ul>
              <li>Free trials are non-refundable</li>
              <li>Paid trials are refundable if cancelled more than 12 hours in advance</li>
              <li>Refund requests must be made within 24 hours of trial completion</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>5. Subscription and Package Purchases</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            <ul>
              <li>Subscriptions may receive prorated refunds for unused time</li>
              <li>Package refunds for unused sessions must be requested within 7 days</li>
              <li>Auto-renewal can be cancelled anytime and takes effect at the end of the billing cycle</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>6. Refund Eligibility</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Refunds may be granted for:
            <ul>
              <li>Technical issues</li>
              <li>Classes not delivered</li>
              <li>Quality issues subject to review</li>
              <li>Duplicate or unauthorized payments</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>7. Non-Refundable Items</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            <ul>
              <li>Completed classes except exceptional cases</li>
              <li>Payment gateway fees</li>
              <li>Used wallet top-ups</li>
              <li>Purchases older than 30 days</li>
              <li>Fully utilized services</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>8. Refund Process</Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>8.1 How to Request a Refund</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            <ul>
              <li>Open the Teachora app</li>
              <li>Navigate to bookings or transaction history</li>
              <li>Select the transaction and request a refund</li>
              <li>Provide a reason and submit</li>
            </ul>
            You may also contact support via email.
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>8.2 Review Process</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            <ul>
              <li>Requests are reviewed within 24 to 48 hours</li>
              <li>Additional information may be requested</li>
              <li>Decisions are communicated via email or in-app notification</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3 }}>8.3 Refund Methods</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8, ml: 2 }}>
            <ul>
              <li>Original payment method</li>
              <li>Wallet credit with user consent</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>9. Chargebacks and Disputes</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Chargebacks may result in account suspension during investigation. Users are encouraged to contact support before initiating disputes.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>10. Special Circumstances</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Refunds may be considered for medical emergencies or unforeseen events with supporting documentation.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>11. Wallet and Credits</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            <ul>
              <li>Unused wallet balance may be refunded upon account closure</li>
              <li>Promotional credits are non-refundable</li>
              <li>Wallet credits may expire with prior notice</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>12. Tutor Earnings and Withdrawals</Typography>
          <Typography variant="body1" component="div" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            <ul>
              <li>Withdrawals are processed within 3 to 5 business days</li>
              <li>Tutor earnings may be adjusted if refunds are issued</li>
              <li>Tutors are responsible for refund-related adjustments</li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>13. Dispute Resolution</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Appeals can be submitted with additional information. Final decisions are provided within 5 business days.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>14. Changes to Refund Policy</Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Changes take effect immediately upon posting. Continued use of Teachora indicates acceptance.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>15. Contact Information</Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            For refund-related questions or requests:<br />
            <strong>Email:</strong> rameshbtechit2001@gmail.com
          </Typography>
        </Paper>
      </Container>
    </Layout>
  );
};

export default PrivacyPolicy;
